import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'

import { createRouteVehiclePoller, type RouteVehiclePoller } from '@/services/timbusApi'
import { WorkerUnauthorizedError } from '@/services/workerApi'
import type { BusRoute } from '@/types'

import { LIVE_VEHICLE_REFETCH_INTERVAL, LIVE_VEHICLE_STALE_TIME } from './liveVehicleQueryPolicy'
import { useWorkerAuth } from './useWorkerAuth'

export function useRouteVehiclesQuery(route: ComputedRef<BusRoute | undefined>) {
  const routeId = computed(() => route.value?.id)
  const { session, clearSession } = useWorkerAuth()
  let activePoller: { routeId: number; routeCode: string; poller: RouteVehiclePoller } | undefined

  return useQuery({
    queryKey: computed(() => ['route-vehicles', routeId.value] as const),
    queryFn: async ({ signal }) => {
      const currentRoute = route.value
      if (!currentRoute || !session.value) return []
      if (
        !activePoller ||
        activePoller.routeId !== currentRoute.id ||
        activePoller.routeCode !== currentRoute.code
      ) {
        activePoller = {
          routeId: currentRoute.id,
          routeCode: currentRoute.code,
          poller: createRouteVehiclePoller(currentRoute),
        }
      }
      try {
        return await activePoller.poller.refresh({ signal, session: session.value })
      } catch (error) {
        if (error instanceof WorkerUnauthorizedError) clearSession()
        throw error
      }
    },
    enabled: computed(() => routeId.value !== undefined && session.value !== null),
    refetchInterval: LIVE_VEHICLE_REFETCH_INTERVAL,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: LIVE_VEHICLE_STALE_TIME,
  })
}
